import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/track";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.order_id) setOrderId(data.order_id);
        if (data.payment_status === "paid") { setState("paid"); track("payment_success", { method: "card" }); toast.success("Payment successful!"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (err) { console.warn("payment status poll failed (transient), retrying", err?.message || err); }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const goToOrder = () => navigate(orderId ? `/orders/${orderId}` : "/dashboard", { replace: true });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6" data-testid="payment-success-page">
      <Logo />
      {state === "checking" && (
        <div className="flex flex-col items-center gap-3 text-center" data-testid="payment-checking">
          <Loader2 className="h-10 w-10 animate-spin text-kilted-green" />
          <p className="text-muted-foreground">Confirming your payment…</p>
        </div>
      )}
      {state === "paid" && (
        <div className="flex flex-col items-center gap-3 text-center" data-testid="payment-paid">
          <CheckCircle2 className="h-14 w-14 text-emerald-500" />
          <h1 className="font-heading text-2xl font-extrabold text-kilted-green">Payment received</h1>
          <p className="max-w-sm text-sm text-muted-foreground">Your delivery is confirmed and a courier will be assigned shortly. A confirmation has been sent via SMS.</p>
          <Button onClick={goToOrder} data-testid="view-order-button" className="mt-2 bg-kilted-green hover:bg-kilted-greenLight">View your delivery</Button>
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div className="flex flex-col items-center gap-3 text-center" data-testid="payment-pending">
          <AlertTriangle className="h-14 w-14 text-kilted-gold" />
          <h1 className="font-heading text-2xl font-extrabold text-kilted-green">Still processing</h1>
          <p className="max-w-sm text-sm text-muted-foreground">Your payment is taking a little longer than usual. You can check your delivery status from your dashboard.</p>
          <Button onClick={goToOrder} data-testid="view-order-button" className="mt-2 bg-kilted-green hover:bg-kilted-greenLight">Go to my delivery</Button>
        </div>
      )}
    </div>
  );
}
